import React from "react";
import { shallowEqual } from "react-redux";
import styles from "./Summary.module.scss";
import Text from "../../../../../../shared-components/css-modules/toyota/atoms/Text";
import { CarConfigLabel } from "../../../../../utils/labelUtils";
import Button from "../../../../../../shared-components/css-modules/toyota/atoms/Button";
import Popover from "../../../../../../shared-components/css-modules/toyota/vendor/popover/Popover";
import Icon from "../../../../../../shared-components/css-modules/toyota/atoms/Icon";
import { trackOrderOverviewFinanceTerms, trackOrderOverviewTooltip } from "../../../../../utils/tracking";
import a11y from "../../../../../../shared-components/css-modules/toyota/utils/Accessibility.module.scss";
import { MODAL_FINANCE_CALCULATOR, MODAL_FINANCE_DISCLAIMER } from "../../../../../utils/modalConstants";
import useAphModal from "../../../../../hooks/useAphModal";
import { financeCtaSelector } from "../../../../../utils/selectorUtils";
import { useCarConfigSelector } from "../../../../../redux/store";

const SummaryRepresentativeExample = ({ hidden }: { hidden: boolean }): JSX.Element | null => {
    const financeQuoteTable = useCarConfigSelector(financeCtaSelector, shallowEqual).info.disclaimer?.table;
    const financeQuoteId = useCarConfigSelector((state) => state.finance.quotes.finance);
    const { showModalHelper } = useAphModal();

    if (!financeQuoteTable?.length) return null;

    return (
        <div className={styles["representative-example"]} hidden={hidden}>
            <ul role="list" className={styles["config-sublist"]}>
                <li className={styles["config-subitem"]}>
                    <div className={styles["config-subitem-title"]}>
                        <Text tag="h3" variant="apheleia-h4">
                            <CarConfigLabel label="carConfigRepresentativeExample" />
                        </Text>
                    </div>
                    <div className={styles["config-subitem-price"]}>
                        <Button
                            variant="btn-icon-text-underline"
                            onClick={() => showModalHelper(MODAL_FINANCE_CALCULATOR, {})}
                        >
                            <Icon variant="calculator" sizing="sm" />
                            <CarConfigLabel
                                label={financeQuoteId ? "carConfigEditFinancePlan" : "carConfigOpenFinanceCalculator"}
                            />
                        </Button>
                    </div>
                </li>
                {financeQuoteTable.map((row) =>
                    row ? (
                        <li className={styles["config-subitem"]} key={`${row.name}-${row.value}`}>
                            <div className={styles["config-subitem-title"]}>
                                <Text tag="h4" variant="apheleia-body">
                                    {row.name}
                                    {row.tooltip && (
                                        <Popover
                                            placement="top"
                                            onOpenChangeListen={(isOpen) => {
                                                if (isOpen && row.name) {
                                                    trackOrderOverviewTooltip(row.name);
                                                }
                                            }}
                                        >
                                            <Popover.Trigger>
                                                <Button
                                                    variant="clean"
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                    }}
                                                >
                                                    <Icon variant="info" sizing="md" aria-hidden="true" />
                                                    <span className={a11y["sr-only"]}>
                                                        <CarConfigLabel label="carConfigA11yButtonMoreInfoAbout" />{" "}
                                                        {row.name}
                                                    </span>
                                                </Button>
                                            </Popover.Trigger>
                                            <Popover.Content>{row.tooltip}</Popover.Content>
                                        </Popover>
                                    )}
                                </Text>
                            </div>
                            <Text variant="apheleia-body" className={styles["config-subitem-price"]}>
                                <span dangerouslySetInnerHTML={{ __html: row.value }} />
                            </Text>
                        </li>
                    ) : null,
                )}
                <li className={`${styles["config-subitem"]} ${styles["config-subitem-finance"]}`}>
                    <Text tag="h4" variant="apheleia-body">
                        <Button
                            variant="apheleia-text"
                            className={styles["finance-terms-link"]}
                            onClick={() => {
                                trackOrderOverviewFinanceTerms();
                                showModalHelper(MODAL_FINANCE_DISCLAIMER, {});
                            }}
                        >
                            <CarConfigLabel label="carConfigReadCoreFinanceTerms" />
                        </Button>
                    </Text>
                </li>
            </ul>
        </div>
    );
};

export default SummaryRepresentativeExample;
